/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {Crumb} from '@ion/core/src/types/entities';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {BoundaryMode, CreateGeoFenceTypeEnum} from '../../types/types';
import {removeActiveFence} from '../helpers/MapViewUtils';
import {DrawGeoFence} from './DrawGeoFence';
import {PolygonGeoFence} from './PolygonGeoFence';
import {RadialGeoFence} from './RadialGeoFence';

type EnterGeofenceLocationProps = GlobalGroupProps & {
  selectedGeofenceType: CreateGeoFenceTypeEnum;
  onBoundarySelected?: Function;
  boundaryMode?: BoundaryMode;
  isBoundary: boolean;
  onApplyBoundary?: Function;
  map: __esri.Map;
  mapView: __esri.MapView;
  isVisible: boolean;
  draw: Function;
  clickNext: boolean;
  onDrawingComplete?: Function;
  assetCategories?: string[];
  geofenceSave?: Function;
  selectCrumbs?: Function;
  crumbs?: Crumb[];
  savingGeofence?: boolean;
  mapViewEl?: HTMLElement;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.Layer;
  };
  selectedMapPointRef?: {
    current: null | __esri.Layer;
  };
  editGeofenceGraphic?: __esri.Graphic;
  mapClickHandler?: any;
  setMapClickHandler?: (val: any) => void;
  handleBack?: Function;
  closeGeoFencePanel?: Function;
  useHereApi?: boolean;
  isPhone?: boolean;
  isFMTracking?: boolean;
  toggleShowBoundaryPanel?: Function;
};

export function EnterGeofenceLocation({
  map,
  mapView,
  boundaryMode,
  selectedGeofenceType,
  isVisible,
  onDrawingComplete,
  assetCategories,
  geofenceSave,
  selectCrumbs,
  crumbs,
  savingGeofence,
  mapViewEl,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  editGeofenceGraphic,
  mapClickHandler,
  setMapClickHandler,
  handleBack,
  closeGeoFencePanel,
  isBoundary,
  globalGroup,
  accountMap,
  onApplyBoundary,
  toggleShowBoundaryPanel,
  isPhone,
  useHereApi = false,
  isFMTracking = false
}: EnterGeofenceLocationProps) {
  const timeRef = useRef(8);

  function completeDrawing(details) {
    if (onDrawingComplete) onDrawingComplete(details);
  }

  function cancelDrawing() {
    const index = 0;
    selectCrumbs?.(index, crumbs);
  }

  function onSearchTimeChange(time) {
    timeRef.current = time;
  };

  function handleApplyBoundary() {
    const items = geoFencesGraphicsLayerRef.current?.graphics?.items;
    if (selectedMapPointRef?.current) {
      selectedMapPointRef.current = null;
    }
    if (items?.[0].geometry?.rings) {
      onApplyBoundary?.(
        [items[0].geometry.rings[0]], false, boundaryMode === BoundaryMode.Vicinity ? timeRef.current : null
      );
    }
    // clear layers so user can create a new boundary...
    removeActiveFence(map, [geoFencesGraphicsLayerRef, sketchViewModelRef]);
    mapClickHandler?.remove();
    cancelDrawing();
    closeGeoFencePanel?.();
  }

  const {Radial, Polygon, Draw} = CreateGeoFenceTypeEnum;
  const isRadial  = selectedGeofenceType === Radial;
  const isPolygon = selectedGeofenceType === Polygon;
  const isDraw    = selectedGeofenceType === Draw;

  return (
    <>
      {isRadial && (
        <RadialGeoFence map={map}
          isFMTracking={isFMTracking}
          useHereApi={useHereApi}
          accountMap={accountMap}
          globalGroup={globalGroup}
          isBoundary={isBoundary}
          boundaryMode={boundaryMode}
          onSearchTimeChange={onSearchTimeChange}
          handleApplyBoundary={handleApplyBoundary}
          view={mapView}
          completeDrawing={completeDrawing}
          closeGeoFencePanel={closeGeoFencePanel}
          isVisible={isVisible}
          handleBack={handleBack}
          assetCategories={assetCategories}
          geofenceSave={geofenceSave}
          toggleShowBoundaryPanel={toggleShowBoundaryPanel}
          selectCrumbs={selectCrumbs}
          crumbs={crumbs}
          savingGeofence={savingGeofence}
          mapViewEl={mapViewEl}
          geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
          sketchViewModelRef={sketchViewModelRef}
          selectedMapPointRef={selectedMapPointRef}
          cancelDrawing={cancelDrawing}
          mapClickHandler={mapClickHandler}
          setMapClickHandler={setMapClickHandler}
          isPhone={isPhone} />
      )}
      {isPolygon && (
        <PolygonGeoFence map={map}
          view={mapView}
          isFMTracking={isFMTracking}
          useHereApi={useHereApi}
          accountMap={accountMap}
          isBoundary={isBoundary}
          globalGroup={globalGroup}
          handleApplyBoundary={handleApplyBoundary}
          completeDrawing={completeDrawing}
          closeGeoFencePanel={closeGeoFencePanel}
          isVisible={isVisible}
          toggleShowBoundaryPanel={toggleShowBoundaryPanel}
          handleBack={handleBack}
          assetCategories={assetCategories}
          geofenceSave={geofenceSave}
          selectCrumbs={selectCrumbs}
          crumbs={crumbs}
          savingGeofence={savingGeofence}
          mapViewEl={mapViewEl}
          geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
          sketchViewModelRef={sketchViewModelRef}
          selectedMapPointRef={selectedMapPointRef}
          cancelDrawing={cancelDrawing}
          mapClickHandler={mapClickHandler}
          setMapClickHandler={setMapClickHandler}
          isPhone={isPhone} />
      )}
      {isDraw && (
        <DrawGeoFence map={map}
          accountMap={accountMap}
          view={mapView}
          isFMTracking={isFMTracking}
          useHereApi={useHereApi}
          isBoundary={isBoundary}
          globalGroup={globalGroup}
          toggleShowBoundaryPanel={toggleShowBoundaryPanel}
          handleApplyBoundary={handleApplyBoundary}
          completeDrawing={completeDrawing}
          closeGeoFencePanel={closeGeoFencePanel}
          isVisible={isVisible}
          handleBack={handleBack}
          assetCategories={assetCategories}
          geofenceSave={geofenceSave}
          selectCrumbs={selectCrumbs}
          crumbs={crumbs}
          savingGeofence={savingGeofence}
          mapViewEl={mapViewEl}
          editGeofenceGraphic={editGeofenceGraphic}
          geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
          sketchViewModelRef={sketchViewModelRef}
          selectedMapPointRef={selectedMapPointRef}
          cancelDrawing={cancelDrawing}
          mapClickHandler={mapClickHandler}
          setMapClickHandler={setMapClickHandler}
          isPhone={isPhone} />
      )}
    </>
  );
}
